import React, { useState } from 'react'
import { UPDATE_CHART } from 'Apollo/Mutations/UpdateChart'
import { useMutation } from '@apollo/client'
import { CHART } from 'Apollo/Queries/Chart'
import domPurify from 'dompurify'
import { Modal } from '@material-ui/core'
import TextField from '@mui/material/TextField'
import { Button } from '@resiliatech/birdsong'
import {
  Goal,
  MetricType,
  GoalUpdateInput,
  GoalType,
  Chart,
} from 'Generated/graphql-types'
import {
  Buttons,
  EditChartModalContainer,
  Header,
  Inputs,
} from './EditChartModal.styled'

type EditChartModalProps = {
  goal: Goal
  isOpen: boolean
  onClose: () => void
  onEditChart: (goalUpdateInfo: GoalUpdateInput) => Promise<void>
  chart?: Chart
}

export const EditChartModal = ({
  goal,
  isOpen,
  onClose,
  onEditChart,
  chart,
}: EditChartModalProps) => {
  const [target, setTarget] = useState(chart?.target)
  const [unitOfMeasurement, setUnitOfMeasurement] = useState(
    chart?.unitOfMeasurement || ''
  )
  const isCampaignGoal = goal.type === GoalType.Campaign
  const [chartTitle, setChartTitle] = useState(chart?.title || '')

  const [updateChart] = useMutation(UPDATE_CHART, {
    refetchQueries: [{ query: CHART, variables: { goalId: chart?.goalId } }],
    awaitRefetchQueries: true,
  })

  const handleClose = () => {
    onClose()
  }

  const handleEditChart = async () => {
    if (chart) {
      await updateChart({
        variables: {
          data: {
            chartId: chart.id,
            target,
            title: chartTitle,
            calculationType: chart.calculationType,
            unitOfMeasurement,
          },
        },
      })
      onClose()
    } else {
      onEditChart({
        id: goal.id,
        target,
        unitOfMeasurement,
        metric: goal.metric || MetricType.Total,
      })
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      disableEnforceFocus
    >
      <EditChartModalContainer>
        <Header>Edit Chart</Header>
        <Inputs>
          <TextField
            id='editChartModalInputTitle'
            label='Chart Title'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(chartTitle)}
            onChange={({ target: { value } }) => setChartTitle(value)}
          />
        </Inputs>
        {!isCampaignGoal && (
          <Inputs>
            <TextField
              id='editChartModalInputUnitOfMeasurement'
              label='Unit of Measurement'
              variant='outlined'
              size='small'
              value={domPurify.sanitize(unitOfMeasurement)}
              onChange={({ target: { value } }) => setUnitOfMeasurement(value)}
              required={!isCampaignGoal}
            />
          </Inputs>
        )}
        <Inputs>
          <TextField
            id='editChartModalInputTarget'
            label='What is your target number?'
            variant='outlined'
            size='small'
            type='number'
            value={target}
            onChange={({ target: { value } }) => setTarget(parseInt(value, 10))}
          />
        </Inputs>
        <Buttons>
          <Button
            label='Cancel'
            onClick={handleClose}
            type='button'
            variant='tertiary'
          />
          <Button
            label='Update'
            onClick={handleEditChart}
            type='button'
            variant='primary'
            isDisabled={!unitOfMeasurement && !isCampaignGoal}
          />
        </Buttons>
      </EditChartModalContainer>
    </Modal>
  )
}
